import { useAssetComponents } from "@/hooks/useAssetComponents";
import useLocalMode from "@/hooks/useLocalMode";
import useZoomControls from "@/hooks/useZoomControls";
import { cn } from "@/lib/utils";
import {
  useCanvasStore,
  useCanvasZoomRealtime,
} from "@/store/zustand/canvasStore";
import useLayoutStore from "@/store/zustand/layoutStore";
import { useActiveCanvas } from "@/utils/utils";
import {
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  MinusIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import {
  Check,
  ChevronRight,
  ComponentIcon,
  HomeIcon,
  RefreshCw,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useChangeLedgerStore } from "../../store/zustand/changeLedger";
import DesignToolsPicker from "../DesignToolsPicker";
import { Button } from "../ui/button";
import LocalModeIndicator from "./components/LocalModeIndicator";
import { useUser } from "@clerk/clerk-react";

interface CanvasOverlayProps {}

export const CanvasOverlay: React.FC<CanvasOverlayProps> = React.memo(() => {
  const { isLocalMode } = useLocalMode();
  const { user } = useUser();

  const zoomPerc = useCanvasZoomRealtime();
  const setCanvasPosValue = useCanvasStore((state) => state.setCanvasPosValue);
  const [internalZoomPerc, setInternalZoomPerc] = useState("100%");
  const { zoomIn, zoomOut } = useZoomControls();

  const activeCanvas = useActiveCanvas();

  const flushing = useChangeLedgerStore((state) => state.flushing);
  const ledger = useChangeLedgerStore((state) => state.ledger);
  const triggerFlush = useChangeLedgerStore((state) => state.triggerFlush);

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey
  );
  const setCanvasComponentKey = useCanvasStore(
    (store) => store.setCanvasComponentKey
  );

  const { componentKeyToComponent } = useAssetComponents();

  // Failed instant update where the API hasn't completed yet
  const failedInstantUpdateForChangeInProgress = useMemo(() => {
    return Boolean(
      ledger.find((item) => {
        return !item.wasInstantUpdateSuccessful() && item.needToProcessChange();
      })
    );
  }, [ledger]);

  const showSyncing =
    failedInstantUpdateForChangeInProgress || flushing || triggerFlush;

  useEffect(() => {
    if (typeof zoomPerc !== "undefined") {
      const decorated = Math.round(zoomPerc * 100) + "%";
      setInternalZoomPerc(decorated);
    }
  }, [zoomPerc]);

  const leftExpanded = useLayoutStore((state) => state.leftExpanded);
  const rightExpanded = useLayoutStore((state) => state.rightExpanded);
  const addContextMode = useLayoutStore((state) => state.addContextMode);
  const layoutMode = useLayoutStore((state) => state.layoutMode);

  const expandLeft = useLayoutStore((state) => state.expandLeft);
  const expandRight = useLayoutStore((state) => state.expandRight);

  const collapseLeft = useLayoutStore((state) => state.collapseLeft);
  const collapseRight = useLayoutStore((state) => state.collapseRight);

  const canFullscreen = leftExpanded || rightExpanded;

  const isComponentCanvas = Boolean(canvasComponentKey);

  const showModal = useSelector((state: any) => state.globalSettings.showModal);

  return (
    <div
      className="absolute flex flex-col justify-between items-center pointer-events-none top-0 left-0 right-0 bottom-0"
      style={{
        zIndex: 10006,
      }}
    >
      <div className="w-full flex flex-col">
        <div
          className={cn(
            "w-full flex justify-between items-center p-3",
            isComponentCanvas && "border-b pointer-events-auto bg-secondary-200"
          )}
        >
          <div className="flex gap-2 items-center justify-start">
            {isLocalMode && <LocalModeIndicator />}
            {isComponentCanvas && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 hover:bg-secondary-500 flex gap-1 text-secondary-900 hover:text-white"
                  onClick={() => {
                    // @ts-expect-error TS2345
                    setCanvasComponentKey(null);
                  }}
                >
                  <HomeIcon className="h-3 w-3" />
                  Home
                </Button>
                <ChevronRight className="w-4 h-4 text-secondary-900" />
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 text-primary-400 flex gap-1 cursor-default"
                >
                  <ComponentIcon className="stroke-primary-400 h-3 w-3" />
                  {/*
                   // @ts-expect-error TS2538 */}
                  {componentKeyToComponent?.[canvasComponentKey]?.componentName}
                </Button>
              </>
            )}
          </div>
          {["petergok@hotmail.com", "paarth.mdn@gmail.com"].includes(
            // @ts-expect-error TS2345
            user?.primaryEmailAddress?.emailAddress
          ) && (
            <div className="w-1/4 flex justify-end items-center pointer-events-auto">
              <div className="flex flex-row space-x-1 text-gray-600">
                <div className="bg-secondary-500 text-white shadow-md flex flex-row text-xs rounded-sm p-[1px]">
                  <div className="py-1 px-1 flex justify-center items-center">
                    {showSyncing ? (
                      <RefreshCw className="animate-spin w-3 h-3" />
                    ) : (
                      <Check className="w-3 h-3" />
                    )}
                  </div>
                  <div
                    className={cn(
                      showSyncing ? "w-12 mr-1" : "w-0",
                      "flex justify-center items-center transition-all overflow-hidden"
                    )}
                  >
                    {showSyncing ? "Syncing" : ""}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex flex-row items-end pt-3 relative justify-center pb-6">
          <div className="z-10">
            <DesignToolsPicker />
          </div>
          <div
            className={cn(
              "flex justify-end items-center pointer-events-auto absolute left-6 bottom-6"
            )}
          >
            <div className="flex flex-row space-x-1 text-gray-600">
              <div className="bg-background-tertiary shadow-md text-white flex flex-row text-xs rounded-sm divide-x divide-secondary-200 overflow-hidden">
                {canFullscreen ? (
                  <div
                    className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent h-7 w-7"
                    onClick={() => {
                      collapseLeft();
                      collapseRight();
                    }}
                  >
                    <EnterFullScreenIcon />
                  </div>
                ) : (
                  <div
                    className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent h-7 w-7"
                    onClick={() => {
                      expandLeft();
                      expandRight();
                    }}
                  >
                    <ExitFullScreenIcon />
                  </div>
                )}
              </div>
              <div className="bg-background-tertiary shadow-md text-white flex flex-row text-xs rounded-sm divide-x divide-secondary-200 overflow-hidden">
                <div
                  className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent h-7 w-7"
                  onClick={() => zoomOut()}
                >
                  <MinusIcon />
                </div>
                <div className="w-12 flex justify-center items-center cursor-pointer h-7">
                  <input
                    className="w-full h-full bg-transparent focus:outline-none py-1 text-center text-2xs focus-visible::ring-1 focus-visible:ring-ring focus-visible:outline-none focus:border focus:border-primary-0 hover:bg-background-accent"
                    value={internalZoomPerc}
                    onChange={(e) => setInternalZoomPerc(e.currentTarget.value)}
                    onBlur={() => {
                      let newVal = parseInt(
                        internalZoomPerc.replace(/\D/g, "")
                      );
                      if (!Number.isNaN(newVal)) {
                        // @ts-expect-error TS2345
                        setCanvasPosValue(activeCanvas?.id, {
                          zoomPerc: newVal / 100,
                        });
                      }
                    }}
                    onFocus={(e) => e.currentTarget.select()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                    }}
                  ></input>
                </div>
                <div
                  className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent h-7 w-7"
                  onClick={() => zoomIn()}
                >
                  <PlusIcon />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
